import { useCallback } from "react";
import { Navigate, Outlet } from "react-router";
import { useAtom, useAtomValue } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import useNotificationsWebSocket, {
  type ChatThreadUpdatedEvent,
  type NotificationsReadEvent,
} from "@/features/notifications/hooks/useNotificationsWebSocket";
import { applyNotificationsRead } from "@/features/notifications/lib/applyNotificationsRead";
import { applyThreadUpdate } from "@/features/chat/lib/applyThreadUpdate";
import { MaintenanceBanner } from "@/features/notifications/components/MaintenanceBanner";
import { OptimizationProgressBanner } from "@/features/notifications/components/OptimizationProgressBanner";
import { BuildRouteProgressBanner } from "@/features/orders/components/BuildRouteProgressBanner";
import { useBuildRoutePoller } from "@/features/orders/hooks/useBuildRoutePoller";
import { GeocodeProgressBanner } from "@/features/orders/components/GeocodeProgressBanner";
import { useGeocodeProgressPoller } from "@/features/orders/hooks/useGeocodeProgressPoller";
import { BuildRoutesSummaryAlert } from "@/features/routes/components/BuildRoutesSummaryAlert";
import { UnassignedBuildAlert } from "@/features/routes/components/UnassignedBuildAlert";
import { useRouteStatusLiveUpdates } from "@/features/routes/hooks/useRouteStatusLiveUpdates";
import { ImpersonationBanner } from "@/features/admin/components/ImpersonationBanner";
import { useExitImpersonation } from "@/features/admin/hooks/useExitImpersonation";
import { useAuthStore } from "@/store/auth";
import { isImpersonatingAtom } from "@/store/impersonation";
import { sidebarMobileOpenAtom } from "@/store/sidebar";
import { BrandFooter } from "./BrandFooter";
import { ChromeBannerStrip } from "./ChromeBannerStrip";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";

/**
 * DispatcherShellBody — the dispatcher chrome (sidebar, header, banners) and the
 * dispatcher-only background hooks (notifications WebSocket + build-route
 * poller).
 *
 * Split out from {@link AppShell} so these hooks are MOUNTED ONLY for users that
 * actually belong in the dispatcher shell. AppShell redirects superadmin/dealer
 * users away before rendering this body, so their panels never instantiate the
 * dispatcher polling/WS providers — preventing the tenant-scoped 403 requests
 * BUG-215 reported on dealer login.
 */
function DispatcherShellBody() {
  const queryClient = useQueryClient();
  // TASK-5043: the shell owns the tenant zone for the chrome it renders, and
  // passes it to `Header` as a prop. The hook is safe HERE (unlike inside
  // `Header`) because this body already calls `useQueryClient`, so it cannot
  // render outside a `QueryClientProvider`; `Header`'s own test renders it with
  // no provider, which is why that one takes the zone as a prop.
  const tenantTz = useTenantTimezone();
  const [mobileOpen, setMobileOpen] = useAtom(sidebarMobileOpenAtom);
  const isImpersonating = useAtomValue(isImpersonatingAtom);
  const handleExitImpersonation = useExitImpersonation();

  const handleNewNotification = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }, [queryClient]);

  const handleBatchDone = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    void queryClient.invalidateQueries({ queryKey: ["routes"] });
    void queryClient.invalidateQueries({ queryKey: ["route"] });
    void queryClient.invalidateQueries({ queryKey: ["route-stops"] });
    void queryClient.invalidateQueries({ queryKey: ["route-geometry"] });
    void queryClient.invalidateQueries({ queryKey: ["routes-map"] });
    void queryClient.invalidateQueries({ queryKey: ["route-stops-map"] });
    void queryClient.invalidateQueries({ queryKey: ["route-geometry-map"] });
  }, [queryClient]);

  // BUG-723: the chat list, the sidebar «Чат» badge and the bell follow the
  // tenant's chat live instead of the 30–60 s polls.
  const handleChatThreadUpdated = useCallback(
    (event: ChatThreadUpdatedEvent) => applyThreadUpdate(queryClient, event),
    [queryClient],
  );
  const handleNotificationsRead = useCallback(
    (event: NotificationsReadEvent) =>
      applyNotificationsRead(queryClient, event),
    [queryClient],
  );

  useNotificationsWebSocket({
    onNewNotification: handleNewNotification,
    onBatchDone: handleBatchDone,
    onChatThreadUpdated: handleChatThreadUpdated,
    onNotificationsRead: handleNotificationsRead,
  });

  // BUG-738: live `route_status_update` frames refresh every route view
  // (table, dashboard, maps, chat panel, the route's detail) on ANY page. It
  // was mounted only on the routes list and the route page, so a status change
  // that happened while the dispatcher was elsewhere left the table stale.
  useRouteStatusLiveUpdates();

  // Drive background polling for the build-route Celery task.
  useBuildRoutePoller();

  // Drive background polling for post-import address geocoding (BUG-410).
  // Mounted here (not in the Orders page) so the progress banner survives
  // navigating away from Orders while the staggered Celery fan-out finishes.
  useGeocodeProgressPoller();

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile backdrop — shown when sidebar is open on small screens */}
      {mobileOpen && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <Sidebar />

      {/* Right column: banner (when impersonating) + header + main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Shell chrome banners sit on ONE documented z-layer and publish their
            measured height as `--chrome-offset`, which the fullscreen map
            overlay uses to start BELOW the chrome instead of behind it
            (BUG-462). Two strips because the header sits BETWEEN them: the
            impersonation banner is pinned above the header, the progress
            banners render under it. Their heights sum into one offset. */}
        <ChromeBannerStrip>
          {isImpersonating && (
            <ImpersonationBanner
              onExit={() => void handleExitImpersonation()}
            />
          )}
        </ChromeBannerStrip>
        <Header tenantTz={tenantTz} />
        <ChromeBannerStrip>
          {/* Platform maintenance banner (TASK-3931) — shown in the dispatcher
              shell only; renders nothing when no banner is active. */}
          <MaintenanceBanner />
          <OptimizationProgressBanner />
          <BuildRouteProgressBanner />
          <GeocodeProgressBanner />
        </ChromeBannerStrip>
        {/* `relative` is LOAD-BEARING, not decoration (BUG-484).

            Tailwind's `.sr-only` is `position:absolute`. An absolutely
            positioned box resolves against its nearest POSITIONED ancestor, so
            while this scroll container was `position:static` every bare
            `sr-only` span inside it resolved against the INITIAL containing
            block instead: `offsetParent` became BODY and each span's box was
            placed at page coordinates, inflating `documentElement.scrollHeight`
            past `innerHeight`. That produced a SECOND, window-level scrollbar —
            scrolling it dragged the whole `h-screen` shell off-screen and left
            white void (measured on /orders: scrollHeight 1582 vs innerHeight
            900 = 682px of void, the 1582 being exactly the bottom edge of the
            last escaped span; one span per permanent-order row, so the void
            grew with the list).

            BUG-483 first hit this via Settings hints and fixed it by wrapping
            ONE component (`FieldHint`); the same bug then resurfaced on Orders
            via `PermanentOrderBadge`. Making the scroll container a containing
            block fixes the CLASS — all 29 bare `sr-only` spans across the app
            and any added later — instead of one call site at a time.

            The spans keep `sr-only` verbatim: still absolute, still clipped,
            still in the DOM and exposed to assistive tech (NOT display:none /
            visibility:hidden / aria-hidden), so they remain valid
            `aria-describedby` targets.

            Do NOT use `overflow-hidden` here instead — it clips legitimate UI.
            `fixed` descendants (the fullscreen map overlays on Orders/Routes,
            all Radix overlays) are unaffected: `position:fixed` resolves
            against the viewport, not against a `relative` ancestor. */}
        <main className="relative flex flex-1 flex-col overflow-y-auto bg-gray-50">
          <div className="flex-1 p-6">
            {/*
              Unassigned-build alert is mounted at the shell level so the per-order
              list + reason + actionable hint is visible immediately after a build
              that drops orders, on whatever page the user is redirected to
              (route detail, routes list, etc.). Renders nothing when the atom is
              empty; clears the atom on dismiss.
            */}
            {/*
              Green build-summary panel: after a successful build the dispatcher
              is redirected to /routes and sees each created route as an accordion
              row (vehicle, distance, duration, order count). Shown above the
              unassigned alert so a partial auto-build surfaces both. Renders
              nothing when the atom is empty; clears the atom on dismiss.
            */}
            <div className="mb-4 space-y-4 empty:mb-0">
              <BuildRoutesSummaryAlert />
              <UnassignedBuildAlert />
            </div>
            <Outlet />
          </div>
          <BrandFooter />
        </main>
      </div>
    </div>
  );
}

/**
 * AppShell wraps all authenticated dispatcher pages.
 *
 * Provides ProtectedRoute logic (redirects to /login if not authenticated) and
 * acts as a role gate: superadmin (without an active impersonation) and dealer
 * users are redirected to their own panels (/admin, /dealer) BEFORE the
 * dispatcher chrome or its background hooks mount. Only dispatcher-shell users
 * (dispatcher, company_admin, driver, or a superadmin actively impersonating a
 * tenant) reach {@link DispatcherShellBody}, where the notifications WebSocket
 * and build-route poller live.
 *
 * Gating here (not mounting the hooks at all for admin/dealer) is what keeps
 * the dispatcher-only, tenant-scoped polling out of the AdminShell/DealerShell
 * panels — fixing the 403 request noise BUG-215 reported on dealer login.
 */
export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const isImpersonating = useAtomValue(isImpersonatingAtom);
  const token = localStorage.getItem("auth_token");

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Token present but the profile is still hydrating from storage — wait to
  // avoid a flash-redirect (and to avoid mounting dispatcher hooks before we
  // know the role).
  if (!user) {
    return null;
  }

  // Non-dispatcher roles must never mount the dispatcher chrome/hooks. Redirect
  // them to their own panel. A superadmin actively impersonating a tenant is
  // operating as that tenant and legitimately uses the dispatcher shell.
  if (user.role === "superadmin" && !isImpersonating) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (user.role === "dealer") {
    return <Navigate to="/dealer/dashboard" replace />;
  }
  // BUG-555: a driver has no web surface. LoginPage already routes them to
  // /driver-mobile, but that only covers the login hop — a driver who deep-links to
  // /routes (a bookmark, a shared link, a back button) otherwise lands in the full
  // dispatcher shell: sidebar, "Новый маршрут", the notification bell, and a page that
  // then fails every request with 403. Verified in the browser before this guard.
  if (user.role === "driver") {
    return <Navigate to="/driver-mobile" replace />;
  }

  return <DispatcherShellBody />;
}
